// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { PopoverContextProps } from '@iheartjane/reefer';
import {
  Button,
  CheckIcon,
  ChevronDownIcon,
  Flex,
  List,
  Popover,
  TrashIcon,
  Typography,
} from '@iheartjane/reefer';
import range from 'lodash-es/range';
import { useCallback, useMemo } from 'react';

import type { PendingCartProduct } from '@jane/shared/types';
import { getMaxCartQuantity } from '@jane/shared/util';

import { useKioskSpacing } from '../../../hooks';

interface Props {
  onChangeQuantity: (product: PendingCartProduct, quantity: string) => void;
  onRemove: (productId: number) => void;
  product: PendingCartProduct;
}

const StyledQtyButton = styled(Button)(() => {
  const { defaultSpacing, getKioskSpacing } = useKioskSpacing({
    defaultPadSize: 16,
  });

  return {
    border: 'none',
    borderRadius: getKioskSpacing(32, 12),
    paddingBottom: getKioskSpacing(16, 12),
    paddingLeft: defaultSpacing,
    paddingRight: defaultSpacing,
    paddingTop: getKioskSpacing(16, 12),
  };
});

export const CartRowActions = ({
  onChangeQuantity,
  onRemove,
  product,
}: Props) => {
  const { defaultSpacing, isIpad } = useKioskSpacing();

  const quantityOptions = useMemo(
    () =>
      range(1, getMaxCartQuantity(product, product.price_id) + 1).map((q) => ({
        label: q.toString(),
        value: q,
      })),
    [product]
  );

  const onRemoveProduct = useCallback(() => {
    onRemove(product.product_id);
  }, [onRemove, product.product_id]);

  const onUpdateProduct = useCallback(
    (qty: (typeof quantityOptions)[number], qtyProduct: PendingCartProduct) => {
      onChangeQuantity(qtyProduct, qty.label);
    },
    [onChangeQuantity]
  );
  const { getKioskSpacing } = useKioskSpacing();

  return (
    <>
      <Popover
        alignment={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        disableMobileStyling
        label="Select quantity"
        ml={defaultSpacing}
        openOn={quantityOptions.length <= 1 ? 'disabled' : 'click'}
        target={
          <StyledQtyButton
            disabled={quantityOptions.length <= 1}
            endIcon={<ChevronDownIcon size="sm" />}
            label={
              <Typography variant="body-bold">
                {product.count.toString()}
              </Typography>
            }
            size={isIpad ? 'small' : 'default'}
            data-testid={`${product.name}-quantity-btn`}
            variant="tertiary"
          />
        }
      >
        {({ closePopover }: PopoverContextProps) => (
          <Popover.Content padding={false}>
            <List label="quantity selector" py={16}>
              {quantityOptions.map((qty) => (
                <List.Item
                  ariaLabel={qty.label}
                  onClick={() => {
                    onUpdateProduct(qty, product);
                    closePopover();
                  }}
                  key={qty.label}
                  px={24}
                  py={12}
                >
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    flexDirection="row"
                  >
                    <Typography variant="body-bold">{qty.label}</Typography>
                    {product.count === qty.value && <CheckIcon />}
                  </Flex>
                </List.Item>
              ))}
            </List>
          </Popover.Content>
        )}
      </Popover>
      <Button.Icon
        ariaLabel={`Remove ${product.name} from cart`}
        icon={<TrashIcon size={isIpad ? 'md' : 'lg'} />}
        ml={getKioskSpacing(12, 4)}
        onClick={onRemoveProduct}
        variant="minimal"
      />
    </>
  );
};
